import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { concat, iif, of } from 'rxjs';
import {
    catchError,
    map,
    mapTo,
    mergeMap,
    switchMap,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { createTab as createHiTab } from '../human-insight/human-insight.model';
import { Override } from '../insight/config.insight.model';
import { rootOverride } from '../insight/insight.action';
import {
    pluck,
    retrieveOverride,
    updateOverride,
} from '../insight/insight.epic';
import { InsightService } from '../insight/insight.service';
import { BaseState } from '../insight/insight.state';
import { overrideInsight } from '../insight/override-insight.function';
import { TabType } from '../insight/tabset.model';
import { Dependencies } from '../store';

import * as actions from './insight-explorer.action';
import {
    defaultState,
    RootState,
    storeStateMapper,
} from './insight-explorer.state';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootInit = (
    action: ActionsObservable<actions.InitAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT_EXPLORER__ROOT__INIT),
        switchMap((act) => {
            return of({
                root: act,
                override: undefined,
                prevState: undefined,
                state: undefined,
                permissions: undefined,
            }).pipe(
                mergeMap((group) =>
                    of(null).pipe(
                        retrieveOverride<RootState>(deps),
                        tap((data) => {
                            group.override = data.result || {};
                            group.prevState = group.override.extra;
                        }),
                        mapTo(group),
                    ),
                ),
                mergeMap((group) =>
                    deps.injector
                        .get<InsightService>(InsightService)
                        .listPermissions()
                        .pipe(
                            // eslint-disable-next-line no-return-assign
                            tap((data) => (group.permissions = data)),
                            mapTo(group),
                        ),
                ),
                tap((group) => {
                    const def = defaultState();
                    const nextState: RootState = group.prevState || def; // TODO vet and remove invalid
                    nextState.pending = nextState.pending || def.pending;
                    nextState.permissions = group.permissions;

                    if (!nextState.tabs) {
                        nextState.tabs = [];
                    }
                    if (nextState.tabs.length === 0) {
                        const auth =
                            deps.injector.get<AuthService>(AuthService);
                        const hiTab = createHiTab(auth.user);
                        hiTab.ctx.predefined = true;
                        hiTab.ctx.isDefault = true;
                        nextState.tabs.push(hiTab);
                    }
                    nextState.tabs = [
                        ...nextState.tabs.filter((e) => !e.id.startsWith('_')),
                        {
                            id: '_newTab',
                            fixed: true,
                            ctx: {
                                type: TabType.ADD,
                                originalLabel: null,
                                label: '+',
                                config: null,
                            },
                        },
                    ];

                    const defaultTab = nextState.tabs.find(
                        (e) => e.ctx.isDefault === true,
                    );
                    if (defaultTab) {
                        nextState.selectedTab = defaultTab.id;
                    } else if (nextState.tabs.length > 1) {
                        nextState.selectedTab =
                            nextState.tabs[nextState.tabs.length - 2].id;
                    }

                    const { tabs, selectedTab } = overrideInsight({
                        tabs: nextState.tabs,
                        selectedTab: nextState.selectedTab,
                    });
                    nextState.tabs = tabs;
                    nextState.selectedTab = selectedTab;

                    group.state = nextState;
                }),
                mergeMap((group) =>
                    concat(
                        of(rootOverride(group.override)),
                        of(actions.rootInitResult(group.state)),
                        iif(
                            () => group.prevState,
                            of(actions.rootSave(group.state)),
                        ),
                    ),
                ),
                catchError(() => of(actions.rootInitResult())),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSave = (
    action: ActionsObservable<actions.SaveAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(actions.ActionType.INSIGHT_EXPLORER__ROOT__SAVE),
        switchMap((act) =>
            of(act).pipe(
                map((group) => {
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                    const rootState = group.state as RootState;
                    const tabs = rootState.tabs.filter(
                        (e) =>
                            e.ctx.type !== TabType.ADD &&
                            (e.ctx.predefined ||
                                e.ctx.isDefault ||
                                e.ctx.isFavourite),
                    );
                    const override: Override<RootState> = {
                        ...pluck<BaseState>(state.value, 'insight').override,
                        extra: {
                            ...group.state,
                            tabs,
                            permissions: null,
                        },
                    };
                    delete override.extra.permissions;
                    return { override };
                }),
                updateOverride<RootState, { override: Override<RootState> }>(
                    deps,
                ),
                map(() => actions.rootSaveResult(act.state)),
                catchError(() => of(actions.rootSaveResult())),
            ),
        ),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const tabset = (
    action: ActionsObservable<
        | actions.TabsetReorderTabAction
        | actions.TabsetAddTabAction
        | actions.TabsetCloseTabAction
        | actions.TabsetSetTabStateAction
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(
            actions.ActionType.INSIGHT_EXPLORER__TABSET__REORDER_TAB,
            actions.ActionType.INSIGHT_EXPLORER__TABSET__ADD_TAB,
            actions.ActionType.INSIGHT_EXPLORER__TABSET__CLOSE_TAB,
            actions.ActionType.INSIGHT_EXPLORER__TABSET__RENAME_TAB,
            actions.ActionType.INSIGHT_EXPLORER__TABSET__SET_TAB_STATE,
        ),
        switchMap(() => of(actions.rootSave(storeStateMapper(state.value)))),
    );

export default [rootInit, rootSave, tabset];
